class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.selectedCategory = '';

    this.mainSearch = this.querySelector(".search_default");
    this.closeSearch = this.querySelector(".bzsclose");
    this.searchActiveClass = this.dataset.searchActiveClass;

    this.setupEventListeners();
    this.setupCategoryListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    const closeButton = this.querySelector(".bzsclose");
    if (closeButton) {
      closeButton.addEventListener('click', this.onClick.bind(this));
    }
  }

  setupCategoryListeners() {
    const select = this.querySelector('#SearchFilter') || this.querySelector('#MainSearchFilter');
    if (!select) return;

    const params = new URLSearchParams(window.location.search);
    const urlCategory = params.get('category');
    if (urlCategory) {
      for (const option of select.options) {
        if (option.value.toLowerCase() === urlCategory.toLowerCase()) {
          select.value = option.value;
          break;
        }
      }
    }

    this.selectedCategory = select.value?.toLowerCase() || '';

    select.addEventListener('change', (e) => {
      this.setSelectedCategory(e.target.value.toLowerCase());
    });
  }

  isAllCategories(category) {
    const normalized = category?.trim().toLowerCase();
    return !normalized || normalized === 'all categories';
  }

  getCategorySelect() {
    return this.querySelector('#SearchFilter') || this.querySelector('#MainSearchFilter');
  }

  buildCategorySearchUrl(query, category) {
    const trimmedQuery = query.trim();
    const trimmedCategory = category?.trim() || '';
    const predictiveQuery = this.isAllCategories(trimmedCategory)
      ? trimmedQuery
      : `${trimmedQuery} ${trimmedCategory.toLowerCase()}`;

    const params = new URLSearchParams();
    params.set('q', predictiveQuery);
    params.set('type', 'product');
    params.set('options[prefix]', 'last');
    params.set('view_q', trimmedQuery);
    params.set('category', trimmedCategory);

    return `/search?${params.toString()}`;
  }

  buildAllCategoriesSearchUrl(query) {
    const params = new URLSearchParams();
    params.set('q', query.trim());
    params.set('type', 'product');
    params.set('options[prefix]', 'last');
    return `/search?${params.toString()}`;
  }

  setSelectedCategory(category) {
    this.selectedCategory = category;
    const categoryInput = this.querySelector('#predictive-search-category');
    if (categoryInput) {
      categoryInput.value = category;
    }
    if (this.getQuery().length) {
      this.getSearchResults(this.getQuery());
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.mainSearch.classList.contains('hidden'))
    this.mainSearch.classList.add("search_hide");
    this.mainSearch.classList.remove("ps_input");
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.mainSearch.classList.remove("search_hide");
      this.mainSearch.classList.add("ps_input");
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      event.preventDefault();
      return;
    }

    const select = this.getCategorySelect();
    if (!select) return;

    event.preventDefault();

    const url = this.isAllCategories(select.value)
      ? this.buildAllCategoriesSearchUrl(this.getQuery())
      : this.buildCategorySearchUrl(this.getQuery(), select.value);

    window.location.href = url;
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();
    if (!currentSearchTerm.length) {
      this.mainSearch.classList.remove("search_hide");
      this.mainSearch.classList.add("ps_input");
      this.closeSearch.classList.remove("hidden");
      this.hiddenClassActivity(true);
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.hiddenClassActivity(true);
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.mainSearch.classList.add("search_hide");
        this.mainSearch.classList.remove("ps_input");
        this.closeSearch.classList.add("hidden");
        this.hiddenClassActivity(false);
        this.close();
      }
    });
  }

  onClick() {
    setTimeout(() => {
      this.mainSearch.classList.add("search_hide");
      this.mainSearch.classList.remove("ps_input");
      this.closeSearch.classList.add("hidden");
      this.hiddenClassActivity(false);
      this.close();
    })
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g'))?.length > 1) return;
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );

    let activeElementIndex = 0;
    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    for (let i = 0; i < allVisibleElements.length; i++) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
        break;
      }
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];
    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const normalizedCategory = this.selectedCategory?.trim().toLowerCase();
    const queryKey = (searchTerm + (normalizedCategory || '')).replace(/\s+/g, '-').toLowerCase();

    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const query = this.isAllCategories(this.selectedCategory)
      ? searchTerm
      : `${searchTerm} ${this.selectedCategory}`;

    const url = `${routes.predictive_search_url}?q=${encodeURIComponent(query)}&resources[type]=product&section_id=predictive-search`;

    fetch(url, { signal: this.abortController.signal })
      .then((response) => {
        if (!response.ok) {
          this.hiddenClassActivity(false);
          this.close();
          throw new Error(response.status);
        }
        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;

        this.allPredictiveSearchInstances.forEach((instance) => {
          instance.cachedResults[queryKey] = resultsMarkup;
        });

        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) return;
        this.hiddenClassActivity(false);
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');
    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;
    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    this.setLiveRegionResults();
    this.hiddenClassActivity(false);
    this.open();
    this.bindSearchForButton();
  }

  bindSearchForButton() {
    const searchForButton = this.querySelector('#predictive-search-option-search-keywords button');
    if (!searchForButton || searchForButton.dataset.listenerAdded) return;

    searchForButton.dataset.listenerAdded = 'true';
    searchForButton.addEventListener('click', () => {
      const select = this.getCategorySelect();
      const category = select ? select.value : '';
      const url = this.isAllCategories(category)
        ? this.buildAllCategoriesSearchUrl(this.getQuery())
        : this.buildCategorySearchUrl(this.getQuery(), category);
      window.location.href = url;
    });
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(
      this.querySelector('[data-predictive-search-live-region-count-value]')?.textContent || ''
    );
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }

    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
  hiddenClassActivity(isAddOpration = false){
    if(!this.searchActiveClass) return;
    if(!document.body.classList.contains(this.searchActiveClass) && isAddOpration){
        document.body.classList.add(this.searchActiveClass)
    }else if(document.body.classList.contains(this.searchActiveClass) && !isAddOpration){
        document.body.classList.remove(this.searchActiveClass)
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);